import React, { useRef, useState } from 'react';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { ArrowLeft, ArrowRight, Check, Lock, Pencil, X } from 'lucide-react';
import {
  useGetTimeOffEntryByIdQuery,
  useUpdateTimeOffEntryMutation,
  TimeOffEntry,
  TimeOffStatus,
  TimeOffType,
} from '../store/slices/timeOffSlice';
import { useDispatch } from 'react-redux';
import { addNotification, openModal } from '../store/slices/uiSlice';
import { useAuth } from '../contexts/AuthContext';
import { sbg } from '../theme/sbgTokens';
import { AUTH_ROLE_LABELS, AuthRole, isOwnRequestLocked, OWN_REQUEST_LOCKED_REASON } from '../utils/roles';
import { apiErrorReason } from '../components/modals/time-off/timeOffHours';
import { failureReason } from '../utils/requestErrors';
import WfhApprovalRoute from '../components/time-off/WfhApprovalRoute';
import {
  ConfirmDialog,
  DefinitionList,
  EmptyState,
  ErrorState,
  PageHeader,
  PersonAvatar,
  PersonCell,
  SectionCard,
  StageRail,
  StatusPill,
  Tag,
  useDelayedFlag,
} from '../components/ui';
import type { StageState, StatusTone } from '../components/ui';
import { MISSING, formatDate, formatDateRange, formatDateTime, formatDays, formatNumber } from '../utils/format';

// ── Display helpers ──────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'Sick',
  [TimeOffType.WFH]: 'WFH',
};

const STATUS_META: Record<string, { tone: StatusTone; label: string }> = {
  [TimeOffStatus.PENDING]: { tone: 'warning', label: 'Pending approval' },
  [TimeOffStatus.APPROVED]: { tone: 'success', label: 'Approved' },
  [TimeOffStatus.REJECTED]: { tone: 'danger', label: 'Declined' },
};

const REVIEW_REASON_LABEL: Record<string, string> = {
  pto_lead_time: 'Short notice: PTO requested with less than 2 weeks’ notice',
  wfh_not_approved: 'Not on a hybrid schedule: WFH needs VP approval',
  wfh_escalated_to_vp: 'Escalated to a VP for a decision',
  recurring_calendar_series: 'Recurring calendar series',
  uncategorized: 'Needs a category',
  openai_error: 'Couldn’t be categorized automatically',
  reanalyze_failed: 'Couldn’t be categorized automatically',
};

const typeLabel = (type: string) => TYPE_LABEL[type] ?? type;
const statusMeta = (status: string) => STATUS_META[status] ?? { tone: 'neutral' as StatusTone, label: status };

const sentenceCase = (value: string) => {
  const text = value.replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;
};
const reviewReasonLabel = (reason: string) => REVIEW_REASON_LABEL[reason] ?? sentenceCase(reason);

const formatHours = (hours: number | null | undefined) => {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return MISSING;
  return `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)} ${hours === 1 ? 'hour' : 'hours'}`;
};

const isEscalated = (entry: TimeOffEntry) =>
  Boolean(entry.vpEscalatedAt) || entry.reviewReason === 'wfh_escalated_to_vp' || entry.reviewReason === 'wfh_not_approved';

/**
 * A VP review is still open. Same test the API uses before it refuses a
 * manager's approve/decline with 403 ("Only HR or an admin can override an
 * active VP review.").
 */
const isUnderVpReview = (entry: TimeOffEntry) =>
  entry.status === TimeOffStatus.PENDING
  && entry.type === TimeOffType.WFH
  && entry.reviewReason === 'wfh_escalated_to_vp'
  && !entry.vpDecidedAt;

const possessive = (name: string) => (name.endsWith('s') ? `${name}’` : `${name}’s`);

type Decision = TimeOffStatus.APPROVED | TimeOffStatus.REJECTED;

/**
 * "Couldn’t approve Maria Chen’s request." + the API's reason for a refusal
 * ("This request was completed by another reviewer. Refresh to see the
 * current decision."), or a retry when the server failed or didn't answer.
 */
const decisionErrorMessage = (approving: boolean, name: string, err: unknown) =>
  `Couldn’t ${approving ? 'approve' : 'decline'} ${possessive(name)} request. ${failureReason(err)}`;

const roleLabel = (role: string | null | undefined) =>
  role ? AUTH_ROLE_LABELS[role as AuthRole] ?? sentenceCase(role) : undefined;

/** WFH review reasons that mean the VP policy has handled this request. */
const WFH_ROUTE_REASONS = new Set([
  'wfh_not_approved',
  'wfh_escalated_to_vp',
  'wfh_vp_approved',
  'wfh_vp_approved_with_leads',
  'wfh_vp_declined',
  'wfh_hr_approved',
  'wfh_hr_declined',
  'wfh_rescinded',
  'calendar_canceled',
]);

/** A WFH request the VP approval policy holds or held (not approved on arrival). */
const onWfhRoute = (entry: TimeOffEntry) =>
  entry.type === TimeOffType.WFH && (Boolean(entry.vpEscalatedAt) || WFH_ROUTE_REASONS.has(entry.reviewReason || ''));

/**
 * A WFH request's route as the stage rail draws it. It reaches the VPs only
 * when the employee sends it for review from the emailed link, so that's a
 * stage of its own; HR can decide before then (an override).
 */
const wfhWorkflowStages = (entry: TimeOffEntry): { label: string; state: StageState }[] => {
  const sent = Boolean(entry.vpEscalatedAt) && entry.reviewReason !== 'wfh_not_approved';
  if (entry.reviewReason === 'wfh_rescinded' || entry.reviewReason === 'calendar_canceled') {
    return [
      { label: 'Submitted', state: 'done' },
      ...(sent ? [{ label: 'Sent for VP review', state: 'done' as StageState }] : []),
      { label: entry.reviewReason === 'wfh_rescinded' ? 'Withdrawn' : 'Canceled', state: 'stopped' },
    ];
  }
  if (entry.status === TimeOffStatus.APPROVED || entry.status === TimeOffStatus.REJECTED) {
    const outcome: { label: string; state: StageState } = entry.status === TimeOffStatus.APPROVED
      ? { label: 'Approved', state: 'current' }
      : { label: 'Declined', state: 'stopped' };
    return sent
      ? [
          { label: 'Submitted', state: 'done' },
          { label: 'Sent for VP review', state: 'done' },
          { label: 'VP review', state: 'done' },
          outcome,
        ]
      // Decided from the app before it was sent: the manager, a Jr manager,
      // HR or an admin, and the row doesn't say which.
      : [
          { label: 'Submitted', state: 'done' },
          { label: 'Decided in Attendance', state: 'done' },
          outcome,
        ];
  }
  return [
    { label: 'Submitted', state: 'done' },
    { label: 'Sent for VP review', state: sent ? 'done' : 'current' },
    { label: 'VP review', state: sent ? 'current' : 'upcoming' },
    { label: 'Decision', state: 'upcoming' },
  ];
};

/**
 * The request's workflow as the stage rail draws it. Every request is
 * submitted, reviewed (by a VP when it was escalated) and then decided, so
 * the three stages come straight from `status` and the VP fields.
 */
const workflowStages = (entry: TimeOffEntry): { label: string; state: StageState }[] => {
  if (onWfhRoute(entry)) return wfhWorkflowStages(entry);
  const reviewLabel = isEscalated(entry) ? 'VP review' : 'Review';
  if (entry.status === TimeOffStatus.APPROVED) {
    return [
      { label: 'Submitted', state: 'done' },
      { label: reviewLabel, state: 'done' },
      { label: 'Approved', state: 'current' },
    ];
  }
  if (entry.status === TimeOffStatus.REJECTED) {
    return [
      { label: 'Submitted', state: 'done' },
      { label: reviewLabel, state: 'done' },
      { label: entry.reviewReason === 'calendar_canceled' ? 'Canceled' : 'Declined', state: 'stopped' },
    ];
  }
  return [
    { label: 'Submitted', state: 'done' },
    { label: reviewLabel, state: 'current' },
    { label: 'Decision', state: 'upcoming' },
  ];
};

const workflowSentence = (entry: TimeOffEntry) => {
  if (entry.status === TimeOffStatus.APPROVED) {
    return entry.vpDecidedAt
      ? `Approved on ${formatDateTime(entry.vpDecidedAt)}${entry.vpDecisionSource === 'hr_override' ? ' by HR override' : ' by a VP'}.`
      : `Approved. Last changed ${formatDateTime(entry.updatedAt)}.`;
  }
  if (entry.status === TimeOffStatus.REJECTED) {
    if (entry.reviewReason === 'calendar_canceled') return `Canceled in Outlook. Last changed ${formatDateTime(entry.updatedAt)}.`;
    return entry.vpDecidedAt
      ? `Declined on ${formatDateTime(entry.vpDecidedAt)}${entry.vpDecisionSource === 'hr_override' ? ' by HR override' : ' by a VP'}.`
      : `Declined. Last changed ${formatDateTime(entry.updatedAt)}.`;
  }
  if (entry.vpEscalatedAt) {
    return entry.vpDecisionDueAt
      ? `Waiting on a VP. Escalated ${formatDateTime(entry.vpEscalatedAt)}; a decision is due by ${formatDateTime(entry.vpDecisionDueAt)}.`
      : `Waiting on a VP. Escalated ${formatDateTime(entry.vpEscalatedAt)}.`;
  }
  return `Waiting on ${entry.employee.managerName || 'a manager'}. Submitted ${formatDateTime(entry.createdAt)}.`;
};

// ── Loading skeleton (same layout as the page, so nothing jumps) ─────

const DetailSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label="Loading time-off request" aria-busy="true" sx={{ visibility: visible ? 'visible' : 'hidden' }}>
      <span className="sbg-sr-only">Loading time-off request</span>
      <Box aria-hidden="true">
        <Skeleton variant="rounded" width={140} height={14} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="36%" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="48%" height={16} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={112} sx={{ borderRadius: sbg.radius.card, mb: 3 }} />
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' } }}>
          <Skeleton variant="rounded" height={260} sx={{ borderRadius: sbg.radius.card }} />
          <Skeleton variant="rounded" height={260} sx={{ borderRadius: sbg.radius.card }} />
        </Box>
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────

const TimeOffDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile, user } = useAuth();
  const canOverrideVp = profile?.role === 'admin' || profile?.role === 'hr';

  const { data: timeOff, isLoading, error, refetch } = useGetTimeOffEntryByIdQuery(id);
  const [updateTimeOffEntry] = useUpdateTimeOffEntryMutation();

  // The approve/decline confirmation. It lives on this page rather than in the
  // global modal state, which holds plain data only (no callbacks). `decision`
  // outlives `decisionOpen` so the dialog keeps its words while it fades out.
  const [decision, setDecision] = useState<Decision>(TimeOffStatus.APPROVED);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const decidingRef = useRef(false);

  const handleBack = () => {
    navigate('/time-off');
  };

  /** The form saves the edit itself (PUT /time-off/:id with only what changed); this entry refetches after. */
  const handleEdit = () => {
    if (!timeOff) return;
    dispatch(openModal({ type: 'time-off-form', data: { timeOff, isEdit: true } }));
  };

  const handleStatusChange = (newStatus: Decision) => {
    if (!timeOff || newStatus === timeOff.status) return;
    setDecisionError(null);
    setDecision(newStatus);
    setDecisionOpen(true);
  };

  const closeDecision = () => {
    if (decidingRef.current) return;
    setDecisionOpen(false);
  };

  /**
   * Same status update the list and the HR queue send. The mutation
   * invalidates every TimeOff tag, so this entry refetches and the page shows
   * the new status. A refusal keeps the dialog open with the reason.
   */
  const confirmDecision = async () => {
    if (!timeOff || !decisionOpen || decidingRef.current) return;
    const approving = decision === TimeOffStatus.APPROVED;
    const name = timeOff.employee.name;
    decidingRef.current = true;
    setDeciding(true);
    setDecisionError(null);
    try {
      await updateTimeOffEntry({ id: timeOff._id, timeOff: { status: decision } }).unwrap();
      dispatch(
        addNotification({
          type: 'success',
          message: `${approving ? 'Approved' : 'Declined'} ${possessive(name)} ${typeLabel(timeOff.type)} for ${formatDateRange(timeOff.startDate, timeOff.endDate)}.`,
        })
      );
      setDecisionOpen(false);
    } catch (err) {
      setDecisionError(decisionErrorMessage(approving, name, err));
    } finally {
      decidingRef.current = false;
      setDeciding(false);
    }
  };

  const backLink = (
    <Button variant="text" size="small" startIcon={<ArrowLeft size={16} />} onClick={handleBack} sx={{ mb: 2, ml: -1 }}>
      Back to time off
    </Button>
  );

  if (isLoading) {
    return (
      <Box>
        {backLink}
        <DetailSkeleton />
      </Box>
    );
  }

  const errorStatus = error && typeof error === 'object' && 'status' in error ? error.status : undefined;
  const notFound = errorStatus === 404;
  const noAccess = errorStatus === 403;

  if (error || !timeOff) {
    const headerTitle = notFound || !error ? 'Request not found' : noAccess ? 'No access to this request' : 'Couldn’t load this request';
    const reason = apiErrorReason(error);
    return (
      <Box>
        {backLink}
        <PageHeader overline="Time-off request" title={headerTitle} />
        <SectionCard>
          {notFound || !error ? (
            <EmptyState
              title="This request isn’t available"
              body="It may have been removed, or the link is incomplete. Find it again from the time-off list."
              action={
                <Button variant="outlined" onClick={handleBack}>
                  Go to time off
                </Button>
              }
            />
          ) : noAccess ? (
            <EmptyState
              icon={<Lock size={24} />}
              title="You don’t have access to this request"
              body="It belongs to someone who isn’t assigned to you. Ask HR or an admin if you need to see it."
              action={
                <Button variant="outlined" onClick={handleBack}>
                  Go to time off
                </Button>
              }
            />
          ) : (
            <ErrorState
              title={reason ? reason.replace(/\.$/, '') : 'The server didn’t respond'}
              body="Try again in a minute, or find the request from the time-off list."
              onRetry={() => refetch()}
            />
          )}
        </SectionCard>
      </Box>
    );
  }

  const status: { tone: StatusTone; label: string } = isUnderVpReview(timeOff) ? { tone: 'info', label: 'VP review' } : statusMeta(timeOff.status);
  const kind = typeLabel(timeOff.type);
  const dateRange = formatDateRange(timeOff.startDate, timeOff.endDate);
  const isPending = timeOff.status === TimeOffStatus.PENDING;
  const approving = decision === TimeOffStatus.APPROVED;
  // The API refuses both: deciding your own request (only admin and HR may),
  // and settling an open VP review without HR's override.
  const ownLocked = isOwnRequestLocked(timeOff.employee, { role: profile?.role, userId: user?.id });
  const decisionLocked = ownLocked || (isUnderVpReview(timeOff) && !canOverrideVp);
  const lockedReason = ownLocked ? OWN_REQUEST_LOCKED_REASON : 'A VP is reviewing this request. HR can override it.';
  const employee = timeOff.employee;
  const departmentName = employee.department?.name;

  const detailItems: { label: React.ReactNode; value: React.ReactNode }[] = [
    { label: 'Type', value: <Tag>{kind}</Tag> },
    { label: 'Dates', value: <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{dateRange}</Box> },
    {
      label: 'Duration',
      value: (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatHours(timeOff.hours)}
          {Number.isFinite(timeOff.hours) && timeOff.hours > 0 && (
            <Box component="span" sx={{ color: sbg.text.secondary }}>{` · ${formatDays(timeOff.hours / 8)}`}</Box>
          )}
        </Box>
      ),
    },
    { label: 'Source', value: timeOff.isFromCalendar ? (timeOff.isRecurring ? 'Outlook calendar · recurring' : 'Outlook calendar') : 'Added in Attendance' },
    { label: 'Category', value: timeOff.category ? sentenceCase(timeOff.category) : MISSING },
    { label: 'Reason', value: timeOff.reason || MISSING },
  ];
  if (timeOff.reviewReason) {
    detailItems.push({ label: 'Review flag', value: reviewReasonLabel(timeOff.reviewReason) });
  }
  if (timeOff.originalCalendarSubject) {
    detailItems.push({ label: 'Calendar subject', value: timeOff.originalCalendarSubject });
  }

  const recordItems = [
    { label: 'Submitted', value: <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(timeOff.createdAt)}</Box> },
    { label: 'Last changed', value: <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(timeOff.updatedAt)}</Box> },
    {
      label: 'Request ID',
      value: (
        <Tooltip title={timeOff._id}>
          <Typography component="span" variant="mono" sx={{ color: sbg.text.secondary }}>
            {timeOff._id.length > 12 ? `${timeOff._id.slice(0, 8)}…` : timeOff._id}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const employeeItems = [
    { label: 'Department', value: departmentName || MISSING },
    // Most teams are named after their department; only show a team that adds something.
    ...(employee.teamName && employee.teamName !== departmentName ? [{ label: 'Team', value: employee.teamName }] : []),
    { label: 'Manager', value: employee.managerName || MISSING },
    {
      label: 'Email',
      value: employee.email ? (
        <Box
          component="a"
          href={`mailto:${employee.email}`}
          sx={{ color: sbg.text.link, textDecoration: 'underline', textUnderlineOffset: 2, '&:hover': { color: sbg.text.linkHover } }}
        >
          {employee.email}
        </Box>
      ) : MISSING,
    },
  ];

  return (
    <Box>
      {backLink}

      <PageHeader
        overline="Time-off request"
        leading={<PersonAvatar name={employee.name} size={40} />}
        title={`${employee.name} · ${kind}`}
        meta={[
          <StatusPill key="status" tone={status.tone}>{status.label}</StatusPill>,
          <Box key="dates" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{dateRange}</Box>,
          <Box key="hours" component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHours(timeOff.hours)}</Box>,
          `Submitted ${formatDate(timeOff.createdAt)}`,
        ]}
        actions={
          <>
            <Tooltip title={ownLocked ? OWN_REQUEST_LOCKED_REASON : ''}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button variant="outlined" startIcon={<Pencil size={16} />} onClick={handleEdit} disabled={ownLocked}>
                  Edit request
                </Button>
              </Box>
            </Tooltip>
            {isPending && (
              <>
                <Tooltip title={decisionLocked ? lockedReason : ''}>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<X size={16} />}
                      disabled={decisionLocked}
                      onClick={() => handleStatusChange(TimeOffStatus.REJECTED)}
                    >
                      Decline request
                    </Button>
                  </Box>
                </Tooltip>
                <Tooltip title={decisionLocked ? lockedReason : ''}>
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <Button
                      variant="contained"
                      startIcon={<Check size={16} />}
                      disabled={decisionLocked}
                      onClick={() => handleStatusChange(TimeOffStatus.APPROVED)}
                    >
                      Approve request
                    </Button>
                  </Box>
                </Tooltip>
              </>
            )}
          </>
        }
      />

      <Box sx={{ display: 'grid', gap: 3 }}>
        <SectionCard title="Approval" description={timeOff.type === TimeOffType.WFH ? undefined : workflowSentence(timeOff)}>
          <StageRail aria-label="Request progress" stages={workflowStages(timeOff)} sx={{ pt: 0.5 }} />
          {timeOff.type === TimeOffType.WFH && (
            <Box sx={{ mt: 2 }}>
              <WfhApprovalRoute entry={timeOff} layout="card" />
            </Box>
          )}
          {decisionLocked && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 1.5 }}>
              {lockedReason}
            </Typography>
          )}
        </SectionCard>

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
          }}
        >
          <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
            <SectionCard title="Request details">
              <DefinitionList items={detailItems} sx={{ '& > div:last-of-type': { borderBottom: 0 } }} />
            </SectionCard>

            <SectionCard title="Notes">
              {timeOff.notes ? (
                <Typography variant="bodyMd" component="p" sx={{ color: sbg.text.primary, whiteSpace: 'pre-line', maxWidth: '72ch' }}>
                  {timeOff.notes}
                </Typography>
              ) : (
                <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
                  No notes were added to this request.
                </Typography>
              )}
            </SectionCard>
          </Box>

          <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
            <SectionCard
              title="Employee"
              action={
                <Button
                  component={RouterLink}
                  to={`/employees/${employee._id}`}
                  variant="text"
                  size="small"
                  endIcon={<ArrowRight size={14} />}
                >
                  View profile
                </Button>
              }
            >
              <PersonCell name={employee.name} secondary={roleLabel(employee.role)} size={40} sx={{ mb: 1 }} />
              <DefinitionList items={employeeItems} sx={{ '& > div:last-of-type': { borderBottom: 0 } }} />
            </SectionCard>

            <SectionCard title="Record" dense>
              <DefinitionList items={recordItems} sx={{ '& > div:last-of-type': { borderBottom: 0 } }} />
            </SectionCard>
          </Box>
        </Box>
      </Box>

      <ConfirmDialog
        open={decisionOpen}
        onClose={closeDecision}
        onConfirm={confirmDecision}
        title={`${approving ? 'Approve' : 'Decline'} ${possessive(employee.name)} ${kind}?`}
        body={`${kind} for ${dateRange} (${formatHours(timeOff.hours)}) will be marked ${approving ? 'approved' : 'declined'}, and ${employee.name} is emailed.`}
        confirmLabel={approving ? 'Approve request' : 'Decline request'}
        loadingLabel={approving ? 'Approving…' : 'Declining…'}
        destructive={!approving}
        loading={deciding}
      >
        {isUnderVpReview(timeOff) && (
          <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, mt: 1.5 }}>
            A VP is still reviewing this request. Your decision closes that review as an HR override, and the VPs are told.
          </Typography>
        )}
        {decisionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {decisionError}
          </Alert>
        )}
      </ConfirmDialog>
    </Box>
  );
};

export default TimeOffDetail;
